import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  People as PeopleIcon,
  AutoStories as PrayerIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  AdminPanelSettings as AdminIcon,
  PersonOff as PersonOffIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';
import { useAuth } from '../hooks/useAuth';

function AdminDashboard() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');

  useEffect(() => {
    // Only check admin status if user is loaded (not null)
    if (user !== null && user !== undefined) {
      if (!user.is_admin) {
        window.location.href = '/';
        return;
      }

      // User is admin, fetch data
      fetchStats();
      fetchUsers();
      fetchPrayers();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.statistics || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/admin/users?limit=100&search=${searchQuery}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrayers = async () => {
    try {
      const response = await api.get('/admin/prayers?limit=50');
      setPrayers(response.data.prayers || []);
    } catch (error) {
      console.error('Error fetching prayers:', error);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/activate`);
      showSnackbar('User activated successfully', 'success');
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error activating user:', error);
      showSnackbar('Failed to activate user', 'error');
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUser) return;

    try {
      await api.post(`/admin/users/${selectedUser.id}/deactivate`, {
        reason: deactivateReason || 'Violated community guidelines'
      });
      showSnackbar('User deactivated successfully', 'success');
      setDeactivateDialog(false);
      setDeactivateReason('');
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error deactivating user:', error);
      showSnackbar(error.response?.data?.error || 'Failed to deactivate user', 'error');
    }
  };

  const handleMakeAdmin = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/make-admin`);
      showSnackbar('Admin privileges granted', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error granting admin:', error);
      showSnackbar('Failed to grant admin privileges', 'error');
    }
  };

  const handleRemoveAdmin = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/remove-admin`);
      showSnackbar('Admin privileges removed', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
      showSnackbar('Failed to remove admin privileges', 'error');
    }
  };

  const handleDeletePrayer = async (prayerId) => {
    if (!window.confirm('Are you sure you want to delete this prayer?')) {
      return;
    }

    const reason = prompt('Reason for deletion:');
    if (!reason) return;

    try {
      await api.delete(`/admin/prayers/${prayerId}`, {
        data: { reason }
      });
      showSnackbar('Prayer deleted successfully', 'success');
      fetchPrayers();
      fetchStats();
    } catch (error) {
      console.error('Error deleting prayer:', error);
      showSnackbar('Failed to delete prayer', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Show loading while user data is being fetched
  if (user === null || user === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // User is loaded, check if admin
  if (!user.is_admin) {
    return (
      <Container>
        <Alert severity="error">Access Denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  // Show loading while fetching admin data
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Admin Dashboard
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
            onClick={() => setTabValue(0)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total_users || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.active_users || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
            onClick={() => setTabValue(1)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PrayerIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total_prayers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Prayers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BlockIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.inactive_users || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Users Management" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Prayers Moderation" icon={<PrayerIcon />} iconPosition="start" />
        </Tabs>

        {/* Users Management Tab */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Search users by username or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={fetchUsers}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => { setSearchQuery(''); fetchUsers(); }}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.full_name || '-'}</TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Inactive" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Chip label="Admin" color="primary" size="small" icon={<AdminIcon />} />
                        ) : (
                          <Chip label="User" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Prayers Moderation Tab */}
        {tabValue === 1 && (
          <Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Content</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prayers.map((prayer) => (
                    <TableRow key={prayer.id}>
                      <TableCell sx={{ maxWidth: 300 }}>
                        {prayer.content.substring(0, 100)}
                        {prayer.content.length > 100 && '...'}
                      </TableCell>
                      <TableCell>{prayer.username || 'Anonymous'}</TableCell>
                      <TableCell>
                        <Chip label={prayer.category} size="small" />
                      </TableCell>
                      <TableCell>
                        {prayer.created_at ? new Date(prayer.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeletePrayer(prayer.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedUser?.is_active ? (
          <MenuItem
            onClick={() => {
              setDeactivateDialog(true);
              handleMenuClose();
            }}
          >
            <PersonOffIcon fontSize="small" sx={{ mr: 1 }} />
            Deactivate User
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleActivateUser(selectedUser?.id);
              handleMenuClose();
            }}
          >
            <CheckIcon fontSize="small" sx={{ mr: 1 }} />
            Activate User
          </MenuItem>
        )}

        {!selectedUser?.is_admin ? (
          <MenuItem
            onClick={() => {
              handleMakeAdmin(selectedUser?.id);
              handleMenuClose();
            }}
          >
            <AdminIcon fontSize="small" sx={{ mr: 1 }} />
            Make Admin
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleRemoveAdmin(selectedUser?.id);
              handleMenuClose();
            }}
          >
            <PersonOffIcon fontSize="small" sx={{ mr: 1 }} />
            Remove Admin
          </MenuItem>
        )}
      </Menu>

      {/* Deactivate Dialog */}
      <Dialog open={deactivateDialog} onClose={() => setDeactivateDialog(false)}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to deactivate {selectedUser?.username}?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(false)}>Cancel</Button>
          <Button onClick={handleDeactivateUser} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AdminDashboard;
